/* eslint-disable react-refresh/only-export-components */
import React from "react";
import useWebSocketImport, { SendMessage } from "react-use-websocket";

import config from "../utils/config";
import { useAuth } from "./AuthContext";

// react-use-websocket is CommonJS that marks itself `__esModule: true` and puts
// the hook on `exports.default`. Vite 5 unwrapped that; Vite 8 (Rolldown) applies
// Node's ESM/CJS interop instead, so the default import arrives as the module
// namespace ({ default, ReadyState, ... }) rather than the hook itself, and
// calling it throws "is not a function". Unwrap defensively so this keeps working
// under either interop convention.
const useWebSocket =
  typeof useWebSocketImport === "function"
    ? useWebSocketImport
    : (useWebSocketImport as { default: typeof useWebSocketImport }).default;

interface WebsocketContext {
  lastMessage?: Record<string, unknown>;
  sendMessage: SendMessage;
  messages?: MessageShape[];
}

type MessageShape = Record<string, unknown>;

const WebsocketContext = React.createContext<WebsocketContext>({
  sendMessage: () => {},
});

export const WebsocketProvider: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const auth = useAuth();
  const [messages, setMessages] = React.useState<MessageShape[]>([]);
  const { sendMessage, lastJsonMessage } = useWebSocket<MessageShape>(
    config.REALTIME_API_HOST,
    { queryParams: { token: `${auth.accessToken}` } }
  );

  React.useEffect(() => {
    if (lastJsonMessage !== null) {
      setMessages((prev) => prev.concat(lastJsonMessage));
    }
  }, [lastJsonMessage, setMessages]);

  return (
    <WebsocketContext.Provider
      value={{ sendMessage, lastMessage: lastJsonMessage, messages }}
    >
      {children}
    </WebsocketContext.Provider>
  );
};

export const useWebsocket = () => React.useContext(WebsocketContext);
